<template>
  <div :class="$style.container">
    <DolphinMultiSelector
      :class="$style.staffSelector"
      v-model="selectedStaff"
      :options="availableOptions"
      :multiple="false"
      placeholder="担当者を選択して追加"
      track-by="guid"
      label="displayName"
      :show-labels="false"
      :allow-empty="true"
      :close-on-select="true"
      @select="addStaffToDraggable"
    />

    <draggable
      v-model="draggableStaffs"
      item-key="guid"
      :class="$style.draggableList"
      ghost-class="ghostItem"
    >
      <template #item="{ element, index }">
        <div :class="$style.staffItem">
          <span :class="$style.dragHandle">⠿</span>
          <span :class="$style.priorityNumber">{{ index + 1 }}.</span>
          <span :class="$style.staffName">{{ element.displayName }}</span>
          <button
            @click="removeStaffFromDraggable(element.guid)"
            :class="$style.removeButton"
          >
            ×
          </button>
        </div>
      </template>
      <template #footer>
        <div
          v-if="!draggableStaffs || draggableStaffs.length === 0"
          :class="$style.emptyListMessage"
        >
          担当者が選択されていません。上記から追加してください。
        </div>
      </template>
    </draggable>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import draggable from 'vuedraggable'
import DolphinMultiSelector from '@/components/atoms/DolphinMultiSelector.vue'

type StaffMember = {
  guid: string
  displayName: string
  priority?: number
}

const model = defineModel<StaffMember[]>({ required: true })
const props = defineProps<{
  availStaffs: StaffMember[]
}>()

const draggableStaffs = ref<StaffMember[]>(model.value)

onMounted(() => {
  // staffSelectionStrategy が初めて PRIORITY に変更されたとき、
  // priority は undefined なので優先度を初期化する
  if (model.value.some(staff => staff.priority === undefined)) {
    draggableStaffs.value = model.value.map((staff, index) => ({
      ...staff,
      priority: index + 1,
    }))
  }
})

// Filter out already selected staff from available staffs
const availableOptions = computed(() => {
  const selectedGuids = draggableStaffs.value.map(staff => staff.guid)
  return props.availStaffs.filter(
    option => !selectedGuids.includes(option.guid)
  )
})
const selectedStaff = ref<StaffMember | null>(null)

const addStaffToDraggable = (selectedOption: {
  guid: string
  displayName: string
}) => {
  if (!draggableStaffs.value.some(s => s.guid === selectedOption.guid)) {
    draggableStaffs.value = [
      ...draggableStaffs.value,
      {
        ...selectedOption,
        priority: draggableStaffs.value.length + 1,
      },
    ]
  }

  // clear selected staff view from staff selector
  selectedStaff.value = null
}

const removeStaffFromDraggable = (guid: string) => {
  const updatedList = draggableStaffs.value
    .filter(staff => staff.guid !== guid)
    .map((staff, index) => ({
      ...staff,
      priority: index + 1,
    }))

  draggableStaffs.value = updatedList
}

watch(draggableStaffs, () => {
  model.value = draggableStaffs.value.map((staff, index) => ({
    ...staff,
    priority: index + 1,
  }))
})
</script>

<style lang="scss" module>
.container {
  padding-left: 16px;
  padding-right: 16px;
  border-radius: $border-radius-v2-base;
}

.staffSelector {
  margin-bottom: 16px;
}

.draggableList {
  min-height: 60px;
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-base;
  padding: 8px;
  background-color: $color-surface-secondary-opaque;
}

.staffItem {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid $color-border-medium-emphasis;
  background-color: $color-surface-primary;
  margin-bottom: 8px;
  border-radius: $border-radius-v2-base;
  cursor: grab;
  user-select: none; // Prevent text selection while dragging

  &:last-child {
    margin-bottom: 0;
  }

  &.ghostItem {
    // Style for the ghost element during drag
    opacity: 0.5;
    background: $color-blue-200;
  }
}

.dragHandle {
  cursor: grab;
  margin-right: 10px;
  color: $color-text-and-object-low-emphasis;
}

.priorityNumber {
  font-weight: bold;
  margin-right: 10px; // Increased margin
  color: $color-text-and-object-accent-primary; // Use accent color for priority
  min-width: 20px;
  text-align: right;
}

.staffName {
  flex-grow: 1;
  color: $color-text-and-object-high-emphasis;
}

.removeButton {
  background: none;
  border: none;
  color: $color-text-and-object-medium-emphasis;
  cursor: pointer;
  font-size: 18px;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    color: $color-caution-accent;
  }
}

.emptyListMessage {
  padding: 16px;
  text-align: center;
  color: $color-text-and-object-medium-emphasis;
  font-style: italic;
}
</style>
